import { useEffect, useState } from "react";
import { UserCheck, UserX, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const OPTS = [
  ["fit", "Fit for duty", UserCheck, "bg-emerald-600 text-white border-emerald-600"],
  ["off", "Off duty", UserX, "bg-slate-700 text-white border-slate-700"],
  ["issue", "Issue", AlertTriangle, "bg-amber-500 text-white border-amber-500"],
];
const CACHE = "ka_staff_roster";

export function useRoster() {
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    api.get("/checks/staff-roster").then((r) => { setRoster(r.data); localStorage.setItem(CACHE, JSON.stringify(r.data)); })
      .catch(() => setRoster(JSON.parse(localStorage.getItem(CACHE) || "[]")));
  }, []);
  return roster;
}

export const staffComplete = (entries, roster) => !!roster && roster.every((p) => {
  const e = (entries || []).find((x) => x.user_id === p.id);
  return e && (e.status !== "issue" || (e.note?.trim() && typeof e.fixed === "boolean"));
});

export const staffFails = (entries) => (entries || []).some((e) => e.status === "issue" && e.fixed === false);

function Person({ p, e, idx, set }) {
  return (
    <div data-testid={`staff-row-${p.id}`} className={`rounded-xl border p-3 ${e?.status === "issue" ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-white"}`}>
      <div className="mb-2 flex items-baseline justify-between gap-2"><span className="font-semibold text-slate-900">{p.name}</span><span className="text-xs capitalize text-slate-500">{p.tenant_role}</span></div>
      <div className="grid grid-cols-3 gap-1.5">
        {OPTS.map(([k, l, I, on]) => (
          <button key={k} type="button" data-testid={`staff-${idx}-${p.id}-${k}`} onClick={() => set({ status: k })}
            className={`ka-press flex h-11 items-center justify-center gap-1 rounded-lg border text-xs font-semibold ${e?.status === k ? on : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}><I className="h-3.5 w-3.5" />{l}</button>
        ))}
      </div>
      {e?.status === "issue" && (
        <div className="mt-3 space-y-2">
          <textarea data-testid={`staff-${idx}-${p.id}-note`} rows={2} maxLength={300} className="ka-input" placeholder="What's the issue? e.g. cut finger, stomach upset, dirty whites" value={e.note || ""} onChange={(ev) => set({ note: ev.target.value })} />
          <div className="flex gap-1.5">
            {[[true, "Fixed"], [false, "Not fixed"]].map(([v, l]) => (
              <button key={l} type="button" data-testid={`staff-${idx}-${p.id}-fixed-${v}`} onClick={() => set({ fixed: v })}
                className={`ka-press flex-1 rounded-lg border py-2 text-xs font-semibold ${e.fixed === v ? (v ? "border-emerald-600 bg-emerald-600 text-white" : "border-rose-600 bg-rose-600 text-white") : "border-slate-300 bg-white text-slate-700"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function StaffInput({ idx, value, onChange }) {
  const roster = useRoster();
  const entries = Array.isArray(value) ? value : [];
  if (!roster) return <p className="text-sm text-slate-500">Loading team…</p>;
  if (!roster.length) return <p data-testid={`staff-${idx}-empty`} className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">No chefs or staff on the team yet. Add them on the Team page and they'll appear here.</p>;
  const set = (id, patch) => {
    const cur = entries.find((x) => x.user_id === id) || { user_id: id };
    const next = { ...cur, ...patch };
    if (patch.status && patch.status !== "issue") { delete next.note; delete next.fixed; }
    onChange([...entries.filter((x) => x.user_id !== id), next]);
  };
  const allFit = () => onChange(roster.map((p) => entries.find((x) => x.user_id === p.id && x.status !== "fit") || { user_id: p.id, status: "fit" }));
  return (
    <div className="space-y-2">
      <button type="button" data-testid={`staff-${idx}-all-fit`} onClick={allFit} className="text-xs font-semibold text-emerald-700 hover:underline">Mark everyone else fit for duty</button>
      {roster.map((p) => <Person key={p.id} p={p} idx={idx} e={entries.find((x) => x.user_id === p.id)} set={(patch) => set(p.id, patch)} />)}
    </div>
  );
}

export function StaffSummary({ answer }) {
  const label = { fit: "Fit for duty", off: "Off duty", issue: "Issue" };
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${answer.ok ? "border-slate-200" : "border-rose-300 bg-rose-50"}`}>
      <div className="mb-1.5 font-medium">{answer.label}</div>
      <ul className="space-y-1">{(answer.staff || []).map((e) => (
        <li key={e.user_id} className="flex flex-wrap justify-between gap-x-3"><span>{e.name}</span><b className={e.status === "issue" ? (e.fixed ? "text-amber-700" : "text-rose-700") : ""}>{label[e.status]}{e.status === "issue" && ` · ${e.fixed ? "fixed" : "not fixed"}`}</b>{e.note && <span className="w-full text-xs text-slate-600">{e.note}</span>}</li>
      ))}</ul>
      {!answer.staff?.length && <span className="text-slate-500">{answer.value}</span>}
    </div>
  );
}
